import React, { useState, useMemo } from 'react';
import { TATBESTAENDE, KATEGORIEN, type Tatbestand, type TatbestandKategorie } from '../data/tatbestaende';

// ── Hilfsfunktionen ────────────────────────────────────────────────────────

function intervallLabel(monate: number): string {
  if (monate === 12) return 'jährlich';
  if (monate === 24) return '2-jährlich';
  if (monate === 36) return '3-jährlich';
  if (monate === 60) return '5-jährlich';
  return `${monate} Mo.`;
}

const KATEGORIE_COLORS: Record<TatbestandKategorie, string> = {
  Lärm:         'bg-yellow-100 text-yellow-800',
  Vibration:    'bg-orange-100 text-orange-800',
  Staub:        'bg-stone-100  text-stone-800',
  Chemisch:     'bg-red-100    text-red-800',
  Biologisch:   'bg-green-100  text-green-800',
  Physikalisch: 'bg-purple-100 text-purple-800',
  Ergonomisch:  'bg-blue-100   text-blue-800',
  Psychisch:    'bg-pink-100   text-pink-800',
  Allgemein:    'bg-gray-100   text-gray-800',
  Sonder:       'bg-teal-100   text-teal-800',
};

// ── Detailpanel ────────────────────────────────────────────────────────────

function TatbestandDetail({ t, onClose }: { t: Tatbestand; onClose: () => void }): React.JSX.Element {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl max-w-lg w-full p-6 space-y-4"
        onClick={e => { e.stopPropagation(); }}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="font-mono text-xs bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded font-bold text-gray-700 dark:text-gray-300">
                {t.code}
              </span>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${KATEGORIE_COLORS[t.kategorie]}`}>
                {t.kategorie}
              </span>
            </div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t.bezeichnung}</h2>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 flex-shrink-0">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Langtext */}
        <p className="text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
          {t.langtext}
        </p>

        {/* Kennzahlen */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-blue-50 dark:bg-blue-950/30 rounded-lg p-3">
            <p className="text-xs text-blue-600 dark:text-blue-400 font-medium">Intervall</p>
            <p className="text-sm font-bold text-blue-900 dark:text-blue-200">{intervallLabel(t.intervallMonate)}</p>
            <p className="text-xs text-blue-500">{t.intervallMonate} Monate</p>
          </div>
          <div className="bg-green-50 dark:bg-green-950/30 rounded-lg p-3">
            <p className="text-xs text-green-600 dark:text-green-400 font-medium">Gesetzliche Grundlage</p>
            <p className="text-xs font-bold text-green-900 dark:text-green-200 break-words">{t.gesetzlGrundlage}</p>
          </div>
        </div>

        {/* Pflicht-Doku */}
        <div>
          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
            Pflicht-Parameter im Befund
          </p>
          <div className="flex flex-wrap gap-1.5">
            {t.pflichtDoku.map(p => (
              <span key={p} className="text-xs bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 px-2 py-1 rounded-md">
                {p}
              </span>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-2 pt-2 border-t border-gray-100 dark:border-gray-800">
          <button className="btn-primary text-sm flex-1 justify-center" onClick={onClose}>
            Schließen
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Hauptkomponente ────────────────────────────────────────────────────────

export function TatbestandEditor(): React.JSX.Element {
  const [search, setSearch]         = useState('');
  const [filterKat, setFilterKat]   = useState<TatbestandKategorie | 'Alle'>('Alle');
  const [selected, setSelected]     = useState<Tatbestand | null>(null);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return TATBESTAENDE.filter(t => {
      const matchKat  = filterKat === 'Alle' || t.kategorie === filterKat;
      const matchText = q === '' || t.bezeichnung.toLowerCase().includes(q)
                                 || t.code.toLowerCase().includes(q)
                                 || t.langtext.toLowerCase().includes(q);
      return matchKat && matchText;
    });
  }, [search, filterKat]);

  return (
    <div className="p-6 space-y-5 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">VGÜ-Tatbestände</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {TATBESTAENDE.length} Tatbestände gemäß VGÜ-VO BGBl. II Nr. 27/1997 idgF
          </p>
        </div>
        <div className="text-right">
          <span className="text-2xl font-bold text-blue-700 dark:text-blue-400">{filtered.length}</span>
          <p className="text-xs text-gray-400">gefiltert</p>
        </div>
      </div>

      {/* Suche + Filter */}
      <div className="flex flex-col sm:flex-row gap-3">
        {/* Suchfeld */}
        <div className="relative flex-1">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none"
               viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
          </svg>
          <input
            type="search"
            value={search}
            onChange={e => { setSearch(e.target.value); }}
            placeholder="Code, Bezeichnung, Langtext…"
            className="w-full pl-9 pr-4 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700
                       bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100
                       focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Kategorie-Filter */}
        <select
          value={filterKat}
          onChange={e => { setFilterKat(e.target.value as TatbestandKategorie | 'Alle'); }}
          className="px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700
                     bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300
                     focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="Alle">Alle Kategorien</option>
          {KATEGORIEN.map(k => <option key={k} value={k}>{k}</option>)}
        </select>
      </div>

      {/* Kategorien-Chips */}
      <div className="flex flex-wrap gap-2">
        {(['Alle', ...KATEGORIEN] as const).map(k => (
          <button
            key={k}
            onClick={() => { setFilterKat(k); }}
            className={`text-xs px-3 py-1 rounded-full font-medium transition-colors
              ${filterKat === k
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
              }`}
          >
            {k === 'Alle'
              ? `Alle (${TATBESTAENDE.length})`
              : `${k} (${TATBESTAENDE.filter(t => t.kategorie === k).length})`
            }
          </button>
        ))}
      </div>

      {/* Tabelle */}
      <div className="card overflow-hidden p-0">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
            <tr>
              <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3 w-16">Code</th>
              <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3">Bezeichnung</th>
              <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3 hidden sm:table-cell">Kategorie</th>
              <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3 hidden md:table-cell">Intervall</th>
              <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3 hidden lg:table-cell">Grundlage</th>
              <th className="w-10 px-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
            {filtered.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center text-gray-400 py-10 italic">
                  Keine Tatbestände gefunden.
                </td>
              </tr>
            )}
            {filtered.map(t => (
              <tr
                key={t.code}
                className="hover:bg-blue-50 dark:hover:bg-blue-950/20 cursor-pointer transition-colors"
                onClick={() => { setSelected(t); }}
              >
                <td className="px-4 py-3">
                  <span className="font-mono text-xs font-bold text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded">
                    {t.code}
                  </span>
                </td>
                <td className="px-4 py-3 text-gray-900 dark:text-gray-100 font-medium">
                  {t.bezeichnung}
                  <p className="text-xs text-gray-400 font-normal truncate max-w-xs hidden lg:block">
                    {t.langtext}
                  </p>
                </td>
                <td className="px-4 py-3 hidden sm:table-cell">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${KATEGORIE_COLORS[t.kategorie]}`}>
                    {t.kategorie}
                  </span>
                </td>
                <td className="px-4 py-3 text-gray-600 dark:text-gray-400 hidden md:table-cell">
                  {intervallLabel(t.intervallMonate)}
                </td>
                <td className="px-4 py-3 text-xs text-gray-400 font-mono hidden lg:table-cell">
                  {t.gesetzlGrundlage}
                </td>
                <td className="px-2">
                  <svg className="w-4 h-4 text-gray-300 dark:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Detail-Modal */}
      {selected !== null && (
        <TatbestandDetail t={selected} onClose={() => { setSelected(null); }} />
      )}
    </div>
  );
}
